import * as React from 'react';
// import hooks
import {
    useInputCurrency,
    UseInputCurrencyResults,
    useInputDropdown,
    UseInputDropdownResults,
    UseInputFormResult,
} from '@app/hooks/view/input';
// import props
import { 
    FormPopupProps,
    FormPopupMinCostPerSquareFootProps,
    NavigationFooterProps,
 } from '@app/props';
// import models
import { PossibleConstructionType, PossibleState } from '@app/models/api';

import { 
    BroadLineOfBusiness,
    AppThemeColor,
    NavigationFooterAlignment,
    PopupWidth,
 } from '@app/enumerations';
// import api models
import { MinCostPerSquareFoot } from '@app/models/api';

import {
    DropdownItem
} from '@app/models/app';
import { Check, Close } from '@mui/icons-material';

interface UseFormPopupMinCostPerSquareFootParameters {
    id: string;
    props: FormPopupMinCostPerSquareFootProps;
}

interface UseFormPopupMinCostPerSquareFootResults {
    broadLineOfBusinessInput: UseInputDropdownResults;
    constructionTypeInput: UseInputDropdownResults;
    minCostPerSquareFootInput: UseInputCurrencyResults;
    stateInput: UseInputDropdownResults;
    applyFormProps: () => FormPopupProps;
    applyNavigationFooterProps: () => NavigationFooterProps;
}

const useFormPopupMinCostPerSquareFoot = (
    params: UseFormPopupMinCostPerSquareFootParameters
): UseFormPopupMinCostPerSquareFootResults => {
    //#region exported methods
    const applyFormProps = (): FormPopupProps => {
        return {
            fixedNavigation: true,
            id: generateIdForForm(),
            isOpen: params.props.isOpen,
            onCloseClick: params.props.onClose,
            popupWidth: PopupWidth.MEDIUM,
            themeType: AppThemeColor.PRIMARY,
            title: params.props.popupTitle,
        };
    };

    const applyNavigationFooterProps = (): NavigationFooterProps => {
        return {
            id: generateIdForNavigationFooter(),
            leftButtonDisabled: false,
            leftButtonLabel: 'Close',
            leftButtonClick: params.props.onClose,
            leftButtonIcon: Close,
            rightButtonDisabled: false,
            rightButtonLabel: 'Save',
            rightButtonClick: onSave,
            rightButtonIcon: Check,
            alignment: NavigationFooterAlignment.RIGHT,
        };
    };
    //#endregion exported methods

    //#region private methods
    const checkInputValidationWithFocus = <T>(
        input: UseInputFormResult<T>
    ): boolean => {
        const isValid = input.isInputValid;
        if (!isValid) {
            input.focusToInput();
        }
        return isValid;
    };

    const checkFormValidationWithFocus = (): boolean => {
        return checkInputValidationWithFocus<string>(broadLineOfBusinessInput)
            && checkInputValidationWithFocus<number | undefined>(minCostPerSquareFootInput)
            && checkInputValidationWithFocus<string>(stateInput)
            && checkInputValidationWithFocus<string>(constructionTypeInput);
    };

    const flagAllInputsToShowValidations = (): void => {
        constructionTypeInput.changeShowValidationFlag(true);
        broadLineOfBusinessInput.changeShowValidationFlag(true);
        minCostPerSquareFootInput.changeShowValidationFlag(true);
        stateInput.changeShowValidationFlag(true);
    };

    const onSave = (): void => {
        flagAllInputsToShowValidations();
        const isFormValid = checkFormValidationWithFocus();
        if (isFormValid) {
            const minCostPerSquareFoot = new MinCostPerSquareFoot();
            minCostPerSquareFoot.broadLineOfBusinessCode = broadLineOfBusinessInput.value === BroadLineOfBusiness.COMMERCIAL
                ? BroadLineOfBusiness.COMMERCIAL
                : BroadLineOfBusiness.PERSONAL;
            minCostPerSquareFoot.constructionTypeCode = constructionTypeInput.value === '0' ? null : constructionTypeInput.value;
            minCostPerSquareFoot.stateCode = stateInput.value === '0' ? null : stateInput.value;
            minCostPerSquareFoot.minCostPerSquareFoot = minCostPerSquareFootInput.value ?? 0;
            minCostPerSquareFoot.versionId = params.props.minCostPerSquareFoot?.versionId ?? 0;
            params.props.onSave(minCostPerSquareFoot);
        }
    };

    //#region id generators

    const generateIdForForm = (): string => {
        return params.id.toString();
    };

    const generateIdForNavigationFooter = (): string => {
        return `${generateIdForForm()}-navigation-footer`;
    };

    const generateIdForConstructionTypeInput = (): string => {
        return `${generateIdForForm()}-construction-type-input`;
    };

    const generateIdForStateInput = (): string => {
        return `${generateIdForForm()}-state-input`;
    };

    const generateIdForBroadLineOfBusinessInput = (): string => {
        return `${generateIdForForm()}-broad-line-of-business-input`;
    };

    const generateIdForMinCostPerSquareFootInput = (): string => {
        return `${generateIdForForm()}-min-cost-per-square-foot-input`;
    };

    //#endregion id generators

    const getDefaultConstructionType = (): DropdownItem => {
        return new DropdownItem({
            label: params.props.minCostPerSquareFoot === undefined ? 'Select Construction Type' : "Default",
            value: '0',
        });
    };

    const getConstructionTypeItems = (broadLineOfBusiness: string): DropdownItem[] => {
        if (broadLineOfBusiness === '0') {
            return [];
        }
        return params.props.possibleConstructionTypes[broadLineOfBusiness]?.map(
            (constructionType: PossibleConstructionType) => {
                return new DropdownItem({
                    label: constructionType.displayName ?? '',
                    value: constructionType.code ?? '',
                });
            }
        ) ?? [];
    };

    const getDefaultState = (): DropdownItem => {
        return new DropdownItem({
            label: params.props.minCostPerSquareFoot === undefined ? 'Select State' : "All States",
            value: '0',
        });
    };

    const getStateItems = (): DropdownItem[] => {
        return params.props.possibleStates.map((state: PossibleState) => {
            return new DropdownItem({
                label: state.name ?? '',
                value: state.code ?? '',
            });
        });
    };

    const getDefaultBroadLineOfBusiness = (): DropdownItem => {
        return new DropdownItem({
            label: 'Select Broad Line of Business',
            value: '0',
        });
    };

    const getBroadLineOfBusinessItems = (): DropdownItem[] => {
        return Object.keys(BroadLineOfBusiness).filter((k => k !== 'NONE')).map((key: string) => {
            return new DropdownItem({
                label: key,
                value: BroadLineOfBusiness[key],
            });
        });
    };   

    //#endregion private methods

    //#region variables
   
    const stateInput: UseInputDropdownResults = useInputDropdown({
        id: generateIdForStateInput(),
        label: 'State',
        required: false,
        disabled: params.props.minCostPerSquareFoot !== undefined,
        value: params.props.minCostPerSquareFoot?.stateCode ?? String.empty(),
        defaultDropdownItem: getDefaultState(),
        dropdownItems: getStateItems(),
    });

    const broadLineOfBusinessInput: UseInputDropdownResults = useInputDropdown({
        id: generateIdForBroadLineOfBusinessInput(),
        label: 'Broad Line of Business',
        required: true,
        disabled: params.props.minCostPerSquareFoot !== undefined,
        value: params.props.minCostPerSquareFoot?.broadLineOfBusinessCode ?? String.empty(),
        defaultDropdownItem: getDefaultBroadLineOfBusiness(),
        dropdownItems: getBroadLineOfBusinessItems(),
    });

    const dropdownOptions = React.useMemo(() => {
        if (broadLineOfBusinessInput.value === '0') {
            return [];
        }
        return getConstructionTypeItems(broadLineOfBusinessInput.value);
    }, [broadLineOfBusinessInput.value]);

    const constructionTypeInput: UseInputDropdownResults = useInputDropdown({
        id: generateIdForConstructionTypeInput(),
        label: 'Construction Type',
        required: false,
        disabled: params.props.minCostPerSquareFoot !== undefined,
        value: params.props.minCostPerSquareFoot?.constructionTypeCode ?? String.empty(),
        defaultDropdownItem: getDefaultConstructionType(),
        dropdownItems: dropdownOptions,
    });

    const minCostPerSquareFootInput: UseInputCurrencyResults = useInputCurrency({
        id: generateIdForMinCostPerSquareFootInput(),
        label: 'Min Cost Per Square Foot',
        required: true,
        disabled: false,
        value: params.props.minCostPerSquareFoot?.minCostPerSquareFoot ?? 0,
        minValue: 0,
        minValueInclusive: true,
    });

    //#endregion variables

    return {
        broadLineOfBusinessInput,
        constructionTypeInput,
        minCostPerSquareFootInput,
        stateInput,
        applyFormProps,
        applyNavigationFooterProps,
    };
};

//export parameters
export { UseFormPopupMinCostPerSquareFootParameters };

//export results
export { UseFormPopupMinCostPerSquareFootResults };

export default useFormPopupMinCostPerSquareFoot;

